import { useMemo } from 'react';
import HoleSchematic from '../components/HoleSchematic';
import { distanceM, formatDistance } from '../lib/geo';
import type { Hole } from '../lib/types';

/**
 * ONE hole, drawn as polygons, and two buttons. That is the entire screen.
 *
 * This is the deliberately narrow surface: no basemap, no tiles, no routing,
 * no live position, no search, no scorecard. A golfer looks at the shape of
 * the hole and moves to the next one. Everything it draws comes from the
 * course polygons already in the payload, so it renders identically with the
 * radio off, which is why it can run on a watch and a TV from the same route.
 *
 * The two buttons are the same two buttons on all eighteen screens, in the
 * same place, doing the same thing — previous hole and next hole. Nothing is
 * conditional, because a control that appears on some holes and not others is
 * a control a golfer stops trusting.
 */

export interface PolyHoleScreenProps {
  hole: Hole;
  teeColour: string;
  unit: 'm' | 'yd';
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

export default function PolyHoleScreen({
  hole, teeColour, unit, total, onPrev, onNext,
}: PolyHoleScreenProps) {
  /*
   * Card yardage when the club published one, measured tee->green otherwise.
   * Preferring the card matters: a measured centreline on a dogleg is longer
   * than the number on the tee marker, and arguing with the tee marker is a
   * fight the app loses.
   */
  const length = useMemo(() => {
    const card = hole.yardage?.[teeColour];
    if (Number.isFinite(card) && (card as number) > 0) {
      return Math.round(formatDistance(card as number, unit));
    }
    const tee = hole.tees?.[teeColour] ?? Object.values(hole.tees ?? {})[0];
    if (!tee || !hole.green?.middle) return null;
    return Math.round(formatDistance(distanceM(tee, hole.green.middle), unit));
  }, [hole, teeColour, unit]);

  return (
    <div className="poly-hole">
      <header className="ph-head">
        <span className="ph-no">{hole.number}</span>
        <span className="ph-of">of {total}</span>
        <span className="ph-meta">
          PAR {hole.par}
          {length != null && <> · {length}<i>{unit}</i></>}
          {hole.strokeIndex ? ` · SI ${hole.strokeIndex}` : ''}
        </span>
      </header>

      {/* The poly shot. Position and aim are deliberately omitted — with no
          fix passed there is no blue dot, and therefore nothing on this screen
          that could be read as navigation. */}
      <div className="ph-figure">
        <HoleSchematic hole={hole} teeColour={teeColour} />
      </div>

      <footer className="ph-buttons">
        <button className="ph-btn" onClick={onPrev} aria-label="Previous hole">
          <span aria-hidden="true">‹</span>
        </button>
        <button className="ph-btn" onClick={onNext} aria-label="Next hole">
          <span aria-hidden="true">›</span>
        </button>
      </footer>
    </div>
  );
}
